import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiContextFetch } from "@/hooks/api/base"
import { useApiContext } from "@/hooks/api/use-api-context"

export interface UserMappingRow {
  redtail_user_id: string
  name: string
  suggested_email: string | null
  is_importer: boolean
}

export interface BookUser {
  name: string
  email: string
}

export interface UserMappingProposal {
  authenticated_user_id: number | null
  users: UserMappingRow[]
  book_users: BookUser[]
}

export interface UserMappingSelection {
  redtail_user_id: string
  email: string
}

// Map-your-team works against a per-book import, or directly against the source
// before any file/extraction exists (enterprise white-glove).
export interface UserMappingScope {
  dataImportId?: string
  dataImportSourceId?: string
}

function userMappingPath(scope: UserMappingScope) {
  return scope.dataImportSourceId
    ? `data_import_sources/${scope.dataImportSourceId}/user_mappings`
    : `data_imports/${scope.dataImportId}/user_mappings`
}

function scopeId(scope: UserMappingScope) {
  return scope.dataImportSourceId ?? scope.dataImportId ?? ""
}

export function useUserMappingProposal(scope: UserMappingScope) {
  const getContext = useApiContext()

  return useQuery<UserMappingProposal>({
    queryKey: ["user-mappings", scopeId(scope)],
    queryFn: async () => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      return apiContextFetch<UserMappingProposal>(ctx, userMappingPath(scope))
    },
    enabled: !!scopeId(scope),
    retry: false,
  })
}

export function useSaveUserMapping(scope: UserMappingScope) {
  const getContext = useApiContext()
  const qc = useQueryClient()

  return useMutation<void, Error, UserMappingSelection[]>({
    mutationFn: async (userMappings) => {
      const ctx = await getContext()
      if (!ctx) throw new Error("No context found")
      await apiContextFetch<void>(ctx, userMappingPath(scope), {
        method: "POST",
        body: JSON.stringify({ user_mappings: userMappings }),
      })
    },
    onSuccess: () => {
      if (scope.dataImportId) qc.invalidateQueries({ queryKey: ["data-import", scope.dataImportId] })
      qc.invalidateQueries({ queryKey: ["data-import-sources"] })
    },
  })
}
